/**
 * Core UNO Game Types
 *
 * All TypeScript types, interfaces, enums, and constants
 * for the UNO card game implementation.
 */

// ============================================================================
// Card Types
// ============================================================================

export type UnoColor = 'red' | 'blue' | 'green' | 'yellow';
export type UnoWildColor = UnoColor | null;

export type UnoCardType = 'number' | 'skip' | 'reverse' | 'draw-two' | 'wild' | 'wild-draw-four';

export interface UnoCard {
  id: string;
  color: UnoColor | null; // null for wild cards before color is chosen
  type: UnoCardType;
  value: number | null; // 0-9 for number cards, null for action/wild
}

export const UNO_COLORS: UnoColor[] = ['red', 'blue', 'green', 'yellow'];

export const UNO_COLOR_HEX: Record<UnoColor, string> = {
  red: '#e74c3c',
  blue: '#3498db',
  green: '#27ae60',
  yellow: '#f1c40f',
};

// ============================================================================
// Deck Types
// ============================================================================

export interface UnoDeck {
  drawPile: UnoCard[];
  discardPile: UnoCard[];
}

// ============================================================================
// Move Types
// ============================================================================

export interface UnoMove {
  type: 'play' | 'draw' | 'call-uno' | 'challenge-uno';
  card?: UnoCard;
  chosenColor?: UnoColor; // for wild cards
  playerId: string;
}

// ============================================================================
// Player Types
// ============================================================================

export type UnoDifficulty = 'easy' | 'medium' | 'hard';

export interface UnoPlayerState {
  id: string;
  name: string;
  hand: UnoCard[];
  isHuman: boolean;
  aiDifficulty?: UnoDifficulty;
  hasCalledUno: boolean;
  score: number;
}

export interface VisibleUnoPlayerState {
  id: string;
  name: string;
  cardCount: number;
  hand: UnoCard[] | null; // null for opponents (hidden)
  hasCalledUno: boolean;
  score: number;
}

// ============================================================================
// Game State Types
// ============================================================================

export type UnoDirection = 'clockwise' | 'counter-clockwise';

export type UnoGameStatus =
  | { type: 'waiting' }
  | { type: 'active' }
  | { type: 'choosing-color'; playerId: string }
  | { type: 'complete'; winnerId: string };

export interface UnoGameState {
  players: UnoPlayerState[];
  deck: UnoDeck;
  currentPlayerIndex: number;
  direction: UnoDirection;
  topCard: UnoCard;
  chosenColor: UnoColor | null; // color chosen after wild card
  drawStack: number; // cumulative draw count for stacking Draw Two
  gameStatus: UnoGameStatus;
  turnHistory: UnoTurnRecord[];
  roundNumber: number;
}

export interface VisibleUnoGameState {
  players: VisibleUnoPlayerState[];
  currentPlayerIndex: number;
  direction: UnoDirection;
  topCard: UnoCard;
  chosenColor: UnoColor | null;
  drawStack: number;
  gameStatus: UnoGameStatus;
  drawPileCount: number;
  roundNumber: number;
}

// ============================================================================
// Turn Record
// ============================================================================

export interface UnoTurnRecord {
  playerId: string;
  playerName: string;
  action: 'play' | 'draw' | 'draw-and-play' | 'call-uno' | 'forced-draw';
  card?: UnoCard;
  chosenColor?: UnoColor;
  timestamp: number;
}

// ============================================================================
// Game Configuration
// ============================================================================

export interface UnoGameConfig {
  playerCount: number;
  players: UnoPlayerConfig[];
}

export interface UnoPlayerConfig {
  id: string;
  name: string;
  isHuman: boolean;
  aiDifficulty?: UnoDifficulty;
}

// ============================================================================
// Game Settings
// ============================================================================

export interface UnoGameSettings {
  mode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';
  aiDifficulty?: UnoDifficulty;
  aiOpponentCount?: number;
  playerCount?: number;
  isPrivate?: boolean;
}

// ============================================================================
// Game Result and Statistics Types
// ============================================================================

export interface UnoGameResult {
  gameId: string;
  mode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';
  difficulty?: UnoDifficulty;
  finalPosition: number;
  roundsPlayed: number;
  cardsPlayed: number;
  specialCardsPlayed: number;
  unoCallsMade: number;
  duration: number;
  completedAt: number;
}

export interface UnoStatistics {
  userId?: string;
  singlePlayer: {
    easy: UnoGameStats;
    medium: UnoGameStats;
    hard: UnoGameStats;
  };
  localMultiplayer: UnoGameStats;
  onlineMultiplayer: UnoGameStats;
  totalGames: number;
  totalPlayTime: number;
}

export interface UnoGameStats {
  gamesPlayed: number;
  gamesWon: number;
  winPercentage: number;
  totalCardsPlayed: number;
  totalSpecialCardsPlayed: number;
  totalUnoCallsMade: number;
}

// ============================================================================
// Component Props Types
// ============================================================================

export interface UnoBoardProps {
  gameState: VisibleUnoGameState;
  currentPlayerId: string;
  onPlayCard: (card: UnoCard, chosenColor?: UnoColor) => void;
  onDrawCard: () => void;
  onCallUno: () => void;
  isCurrentPlayerTurn: boolean;
  playerHand: UnoCard[];
}

export interface UnoGameModeSelectorProps {
  onStartGame: (settings: UnoGameSettings) => void;
  onJoinPrivateGame?: (roomCode: string) => Promise<void>;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
  isOnline?: boolean;
}

export interface UnoGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

// ============================================================================
// Constants
// ============================================================================

export const INITIAL_HAND_SIZE = 7;
export const DECK_SIZE = 108;
